/**
 * Canonical Channel Partner portal modules (routes that exist in this app).
 * `bookings` covers native bookings against a CP's own ("My Project") inventory only —
 * bookings for IMPORTED (builder-synced) projects remain managed in Builder Admin.
 */
export type CpPortalModuleCategory = 'core' | 'feature' | 'system';

export interface CpPortalModuleDefinition {
  slug: string;
  label: string;
  category: CpPortalModuleCategory;
  primaryFeature: string;
  featureCodes: readonly string[];
}

function cpModule(
  slug: string,
  label: string,
  category: CpPortalModuleCategory,
  featureCodes: readonly string[],
): CpPortalModuleDefinition {
  return {
    slug,
    label,
    category,
    featureCodes,
    primaryFeature: featureCodes[0],
  };
}

const cpFeature = (
  slug: string,
  label: string,
  feature: string,
  extraFeatures: readonly string[] = [],
): CpPortalModuleDefinition =>
  cpModule(slug, label, 'feature', [feature, ...extraFeatures]);

const cpPartnerFeature = (
  slug: string,
  label: string,
  feature: string,
): CpPortalModuleDefinition => cpFeature(slug, label, feature, ['FEAT_CP_MODULE']);

export const CP_PORTAL_MODULES: readonly CpPortalModuleDefinition[] = [
  cpFeature('dashboard', 'Dashboard', 'FEAT_SYS_DASHBOARD'),
  cpPartnerFeature('builders', 'Builders', 'FEAT_MOD_CP_EXPLORE'),
  cpFeature('projects', 'Projects', 'FEAT_MOD_PI_PROJECTS'),
  cpFeature('bookings', 'Bookings', 'FEAT_MOD_CP_BOOKINGS', [
    'FEAT_CP_MODULE',
    'FEAT_MOD_BR_REGISTRATION',
    'FEAT_MOD_FC_DEMAND_PAYMENTS',
    'FEAT_MOD_FC_TRANSACTIONS',
  ]),
  cpFeature('campaigns', 'Campaigns', 'FEAT_MOD_MC_EMAIL'),
  cpFeature('social_hub', 'Social Hub', 'FEAT_MOD_MC_SOCIAL_HUB'),
  cpFeature('persons', 'People', 'FEAT_MOD_CRM_PEOPLE'),
  cpFeature('leads', 'Leads', 'FEAT_MOD_CRM_LEADS'),
  cpFeature('formiq', 'FormIQ', 'FEAT_MOD_CRM_LEADS'),
  cpFeature('opportunities', 'Opportunities', 'FEAT_MOD_CRM_OPPORTUNITY'),
  cpFeature('my-quotations', 'My Quotations', 'FEAT_MOD_CRM_OPPORTUNITY'),
  cpFeature('site-visits', 'Site Visits', 'FEAT_MOD_CRM_SITE_VISIT'),
  cpFeature('calendar-events', 'Meetings', 'FEAT_MOD_CRM_ACTIVITIES'),
  cpFeature('activities', 'Activities', 'FEAT_MOD_CRM_ACTIVITIES'),
  cpPartnerFeature('offers', 'Offers', 'FEAT_MOD_CP_OFFERS'),
  cpPartnerFeature('incentive-reports', 'Incentive Reports', 'FEAT_MOD_CP_INCENTIVES'),
  cpPartnerFeature('invoices', 'Invoices', 'FEAT_MOD_CP_INVOICES'),
  cpPartnerFeature('payments', 'Payments', 'FEAT_MOD_CP_PAYMENTS'),
  cpFeature('price-management', 'Price Management', 'FEAT_MOD_PI_PRICE_MGMT', [
    'FEAT_MOD_PI_PROJECTS',
    'FEAT_SYS_TDS',
    'FEAT_SYS_STAMPDUTY',
    'FEAT_SYS_OTHER_CHARGES',
    'FEAT_MOD_PI_SCHEMES',
  ]),
  cpModule('approval-management', 'Approval Management', 'core', [
    'FEAT_SYS_APPROVAL_MGMT',
  ]),
  cpModule('rules-and-automations', 'Rules & Automations', 'core', [
    'FEAT_CRM_ASSIGNMENT_RULES',
    'FEAT_SYS_APPROVAL_MGMT',
    'FEAT_SYS_GENERIC_MASTERS',
  ]),
  cpModule('scoring-rules', 'Scoring Rules', 'core', ['FEAT_CRM_SCORING_RULES']),
  cpModule('user', 'User Management', 'core', [
    'FEAT_SYS_INTERNAL_USERS',
    'FEAT_SYS_ROLES',
    'FEAT_SYS_DEPARTMENTS',
  ]),
  cpModule('integrations', 'Integrations', 'core', [
    'FEAT_SYS_VENDOR_CONFIG',
    'FEAT_SYS_VENDOR_APIS',
    'FEAT_SYS_API_LOGS',
    'FEAT_MOD_CI_EMAIL',
    'FEAT_MOD_CI_TEMPLATES',
    'FEAT_MOD_CI_DOCUMENT_TEMPLATES',
    'FEAT_MOD_CI_TWILIO',
    'FEAT_MOD_CI_WAPI',
    'FEAT_MOD_CI_SUREPASS',
    'FEAT_MOD_MC_SOCIAL_HUB',
  ]),
  cpModule('bank', 'Bank Management', 'core', [
    'FEAT_MOD_LM_LOANS',
    'FEAT_MOD_LM_BANK_MASTERS',
    'FEAT_MOD_LM_BANK_APF',
    'FEAT_MOD_LM_PRODUCT_MASTERS',
  ]),
  cpModule('companies', 'Companies', 'system', ['FEAT_SYS_COMPANY']),
  cpModule('settings', 'Settings', 'system', ['FEAT_SYS_SETTINGS']),
  cpModule('system', 'Recycle Bin', 'system', ['FEAT_SYS_RECYCLE_BIN']),
  cpModule('audit-logs', 'Audit Logs', 'system', ['FEAT_SYS_AUDIT_LOGS']),
  cpPartnerFeature('my-profile', 'My Profile', 'FEAT_MOD_CP_EXPLORE'),
] as const;

export const CP_PORTAL_MODULE_SLUGS = CP_PORTAL_MODULES.map((module) => module.slug);

export function cpPortalModuleBySlug(
  slug: string,
): CpPortalModuleDefinition | undefined {
  return CP_PORTAL_MODULES.find((module) => module.slug === slug);
}
